import { Injectable } from '@angular/core';
import { SortDirection } from '@common/sort/sort-direction';
import { IPatientListFilterArgs } from 'src/app/common/contracts/filter/patient-list-filter-args';
import { PatientDB } from 'src/app/common/repository/databases';
import { SQLiteQueryBuilder } from 'src/app/common/storage/sqlite-query-builder';
import { PatientList } from 'src/app/patient/components/patient-list/patient-list.enum';
import { SortSelectionItem } from 'src/app/shared/models/sort-selection-item';
import { AccountService } from 'src/app/shared/services/account.service';
import { validate } from 'uuid';

@Injectable()
export class PatientListQueryBuilderService {
  public PAGINATION_DEFAULT = 20;

  constructor(private _accountService: AccountService) {}

  public async getMyActivePatientsQuery(
    skip: number = 0,
    sort: SortSelectionItem
  ): Promise<{ sqlQuery: string; aggregation: any }> {
    const userId = await this._accountService.getCurrentUserId();
    const sqlQuery = new SQLiteQueryBuilder()
      .selectAll()
      .fromFts(PatientDB)
      .where('accountingStatus', '<>', 'false')
      .andGroup(
        new SQLiteQueryBuilder()
          .or('fieldNurseId', '=', userId)
          .or('additionalUserId', '=', userId)
          .or('additionalUserIds', 'LIKE', `%${userId}%`)
      )
      .and('deactivationReason', 'IN', [0, null, 'false'])
      .orderBy(sort.column, sort.direction)
      .innerJoin('patient', 'id', '=', 'key')
      .asString();

    const aggregation = [
      {
        $match: {
          archived: { $ne: true },
          accountingStatus: { $ne: false },
          $or: [{ fieldNurseId: userId }, { additionalUserId: userId }, { additionalUserIds: userId }],
          $and: [
            {
              $or: [
                { deactivationReason: { $eq: 0 } },
                { deactivationReason: { $exists: false } },
                { deactivationReason: { $eq: null } },
              ],
            },
          ],
        },
      },
      {
        $addFields: {
          normalizedCreatedAt: {
            $cond: {
              if: { $eq: [{ $type: '$createdAt' }, 'string'] },
              then: { $dateFromString: { dateString: '$createdAt', onError: '$createdAt' } },
              else: '$createdAt',
            },
          },
        },
      },
      { $setWindowFields: { output: { totalCount: { $count: {} } } } },
      {
        $sort:
          sort.column === 'createdAt'
            ? { normalizedCreatedAt: sort.direction === SortDirection.Ascending ? 1 : -1 }
            : { [sort.column]: sort.direction === SortDirection.Ascending ? 1 : -1 },
      },
      { $skip: skip },
      { $limit: this.PAGINATION_DEFAULT },
    ];

    return { sqlQuery, aggregation };
  }

  public async buildQuery(
    searchTerm: string,
    filterArgs: IPatientListFilterArgs,
    currentList: PatientList
  ): Promise<{
    queryString: string;
    statusQueries?: string[];
    deactivationReasonQueries?: string[];
    regionQueries?: string[];
  }> {
    if (!searchTerm || searchTerm.length <= 2) {
      return null;
    }

    let queryString = this.stripSpaces(searchTerm);
    const statusQueries = [];
    const deactivationReasonQueries = [];
    const regionQueries = [];

    if (!filterArgs && currentList === PatientList.AllPatients) {
      return { queryString };
    }
    for (const field in filterArgs) {
      if (filterArgs.hasOwnProperty(field)) {
        const value = filterArgs[field];
        if (value) {
          if (Array.isArray(value)) {
            if (field === 'status') {
              value.forEach(valueItem => {
                statusQueries.push(`${field}:${valueItem}`);
              });
            } else if (field === 'deactivationReason') {
              value.forEach(valueItem => {
                deactivationReasonQueries.push(`${field}:${valueItem}`);
              });
            } else if (field === 'regions') {
              value.forEach(valueItem => {
                regionQueries.push(`regionId:${valueItem}`);
              });
            }
          } else {
            if (field === 'regions') {
              queryString = `${queryString} regionId:${value}`;
            } else if (field === 'subunitId') {
              queryString = `${queryString} nursingHome.subunitId:${value}`;
            } else {
              queryString = `${queryString} ${field}:${value}`;
            }
          }
        }
      }
    }

    if (currentList === PatientList.MyPatients) {
      queryString = `${queryString} fieldNurseId:${await this._accountService.getCurrentUserId()}`;
    }
    queryString = queryString.trim().replace('***', '');
    return { queryString, statusQueries, deactivationReasonQueries: deactivationReasonQueries, regionQueries };
  }

  private stripSpaces(searchTerm: string): string {
    return searchTerm.replace(/\s{2,}/g, ' ').trim();
  }

  public async buildRawQueries(
    query: string,
    statusQueries: string[],
    deactivationReasonQueries: string[],
    regionQueries: string[],
    sort: SortSelectionItem,
    skip: number = 0,
    showDisabledPatients?: boolean,
    searchDoctorsInIntegratedCares?: boolean
  ): Promise<{ sqlQuery: string; aggregation: any[] }> {
    const sqlQueryBuilder = new SQLiteQueryBuilder();
    sqlQueryBuilder.selectAll().fromFts(PatientDB);
    let $match = {
      archived: { $ne: true },
    };

    // Store doctorId for later use in MongoDB aggregation
    let doctorIdForIntegratedCares: string = null;

    if (!showDisabledPatients) {
      sqlQueryBuilder.where('accountingStatus', '<>', 'false');
      $match['accountingStatus'] = { $ne: false };
    } else {
      sqlQueryBuilder.where('accountingStatus', 'IN', ['true', 'false', null]);
    }

    // if query is a uuid, match on id and clear query
    if (this.isSearchTermUUID(query)) {
      sqlQueryBuilder.and('_id', '=', query);
      $match['_id'] = query;
      query = '';
    }

    // do not split birthday into seperate pieces
    if (query.includes('birthday')) {
      const dateRegex = /birthday:(\d{4}-\d{2}-\d{2})/;
      const matchDate = query.match(dateRegex);
      let extractedDate = '';

      if (matchDate && matchDate[1]) {
        extractedDate = matchDate[1];
        sqlQueryBuilder.and('birthday', '=', extractedDate);
        $match['birthday'] = extractedDate;
      }
      query = query.replace(dateRegex, '').trim();
    }

    // search for fieldNurseId in all possible fields
    if (query.includes('fieldNurseId')) {
      const data = this.matchFieldIdQuery(query, 'fieldNurseId');
      query = data.query;
      const fieldNurseId = data.fieldValue;
      sqlQueryBuilder.andGroup(
        new SQLiteQueryBuilder()
          .or('fieldNurseId', '=', fieldNurseId)
          .or('additionalUserId', '=', fieldNurseId)
          .or('additionalUserIds', 'LIKE', `%${fieldNurseId}%`)
      );
      $match['$or'] = [
        { fieldNurseId: fieldNurseId },
        { additionalUserId: fieldNurseId },
        { additionalUserIds: fieldNurseId },
      ];
    }

    if (query.includes('city')) {
      const data = this.matchFieldIdQuery(query, 'city');
      query = data.query;
      const city = data.fieldValue;
      sqlQueryBuilder.and('city', 'LIKE', `%${city.toLocaleLowerCase()}%`);
      $match['city'] = { $regex: city, $options: 'i' };
    }

    if (query.includes('primaryDoctorId')) {
      const data = this.matchFieldIdQuery(query, 'primaryDoctorId');
      query = data.query;
      const primaryDoctorId = data.fieldValue;

      if (searchDoctorsInIntegratedCares) {
        // Store for MongoDB aggregation later
        doctorIdForIntegratedCares = primaryDoctorId;

        // For SQLite: Check primaryDoctorId OR existence of integrated care with doctorId
        sqlQueryBuilder.andGroup(
          new SQLiteQueryBuilder()
            .or('patient_fts.primaryDoctorId', '=', primaryDoctorId)
            .orRaw(
              `EXISTS (SELECT 1 FROM [integratedCare_fts] WHERE integratedCare_fts.patientId = patient_fts._id AND integratedCare_fts.doctorId = '${primaryDoctorId}')`
            )
        );
        // MongoDB will be handled later with $lookup
      } else {
        sqlQueryBuilder.and('primaryDoctorId', '=', primaryDoctorId);
        $match['primaryDoctorId'] = primaryDoctorId;
      }
    }

    if (query.includes('nursingHomeId')) {
      const data = this.matchFieldIdQuery(query, 'nursingHomeId');
      query = data.query;
      const nursingHomeId = data.fieldValue;
      sqlQueryBuilder.and('nursingHomeId', '=', nursingHomeId);
      $match['nursingHomeId'] = nursingHomeId;
    }

    if (query.includes('nursingServiceId')) {
      const data = this.matchFieldIdQuery(query, 'nursingServiceId');
      query = data.query;
      const nursingServiceId = data.fieldValue;
      sqlQueryBuilder.and('nursingServiceId', '=', nursingServiceId);
      $match['nursingServiceId'] = nursingServiceId;
    }

    if (query.includes('pharmacyId')) {
      const data = this.matchFieldIdQuery(query, 'pharmacyId');
      query = data.query;
      const pharmacyId = data.fieldValue;
      sqlQueryBuilder.and('pharmacyId', '=', pharmacyId);
      $match['pharmacyId'] = pharmacyId;
    }

    if (query.includes('subunitId')) {
      const data = this.matchFieldIdQuery(query, 'nursingHome.subunitId');
      query = data.query;
      const subunitId = data.fieldValue;
      sqlQueryBuilder.and('subunitId', '=', subunitId);
      $match['nursingHome.subunitId'] = subunitId;
    }
    if (deactivationReasonQueries?.length) {
      statusQueries = statusQueries.filter(status => status !== 'status:Deaktiviert');
      if (statusQueries?.length) {
        sqlQueryBuilder.andGroup(
          new SQLiteQueryBuilder()
            .or(
              'deactivationReason',
              'IN',
              deactivationReasonQueries.map(value => parseInt(value.replace('deactivationReason:', ''), 10))
            )
            .or(
              'status',
              'IN',
              statusQueries.map(value => value.replace('status:', '').toLocaleLowerCase())
            )
        );
        $match['$expr'] = {
          $or: [
            {
              $in: [
                '$deactivationReason',
                deactivationReasonQueries.map(value => ({
                  $toInt: value.replace('deactivationReason:', ''),
                })),
              ],
            },
            {
              $in: ['$status', statusQueries.map(value => value.replace('status:', ''))],
            },
          ],
        };
      } else {
        sqlQueryBuilder.and(
          'deactivationReason',
          'IN',
          deactivationReasonQueries.map(value => parseInt(value.replace('deactivationReason:', ''), 10))
        );
        $match = this.createMatchQuery('deactivationReason', deactivationReasonQueries, $match);
      }
    } else {
      if (!statusQueries?.includes('status:Deaktiviert') && !showDisabledPatients) {
        sqlQueryBuilder.and('deactivationReason', 'IN', [0, null, 'false']);
        $match['deactivationReason'] = { $in: [0, null, false] };
      }

      if (statusQueries?.length) {
        sqlQueryBuilder.and(
          'status',
          'IN',
          statusQueries.map(value => value.replace('status:', '').toLocaleLowerCase())
        );
        $match = this.createMatchQuery('status', statusQueries, $match);
      }
    }

    if (regionQueries?.length) {
      sqlQueryBuilder.and(
        'regionId',
        'IN',
        regionQueries.map(value => value.replace('regionId:', ''))
      );
      $match = this.createMatchQuery('regionId', regionQueries, $match);
    }

    // full text search
    let $search;
    if (query.length > 0) {
      query.split(/[\s-]/).map(value => {
        const newSqlQueryBuilder = new SQLiteQueryBuilder();
        newSqlQueryBuilder.or('lastName', 'LIKE', `%${value.toLocaleLowerCase()}%`);
        newSqlQueryBuilder.or('firstName', 'LIKE', `%${value.toLocaleLowerCase()}%`);
        newSqlQueryBuilder.or('customerId', 'LIKE', `%${value.toLocaleLowerCase()}%`);
        newSqlQueryBuilder.or('postalCode', 'LIKE', `%${value.toLocaleLowerCase()}%`);
        newSqlQueryBuilder.or('numberOfInsured', 'LIKE', `%${value.toLocaleLowerCase()}%`);
        sqlQueryBuilder.andGroup(newSqlQueryBuilder);
      });
      const tenantId = await this._accountService.getCurrentUserTenantName();
      // index must be defined in the mongo database for this to work
      // all fields that are defined the index will be searched
      $search = {
        index: `${tenantId}_patient`,
        compound: {
          // split on space and hyphen
          must: query.split(/[\s-]/).map(value => ({
            wildcard: { query: [`*${value}*`], allowAnalyzedField: true, path: { wildcard: '*' } },
          })),
        },
      };
    }

    const sqlQuery = sqlQueryBuilder
      .orderBy(sort.column, sort.direction)
      .innerJoin('patient', 'id', '=', 'key')
      .asString();

    const aggregation = [];
    if ($search) {
      aggregation.push({ $search });
    }

    // Add lookup for integratedCares if searching doctors in integrated cares
    if (searchDoctorsInIntegratedCares && doctorIdForIntegratedCares) {
      aggregation.push({
        $lookup: {
          from: 'integratedCare',
          let: { patientId: '$_id' },
          pipeline: [
            {
              $match: {
                $expr: {
                  $and: [{ $eq: ['$patientId', '$$patientId'] }, { $eq: ['$doctorId', doctorIdForIntegratedCares] }],
                },
              },
            },
          ],
          as: 'matchingIntegratedCares',
        },
      });

      // Filter patients that either have primaryDoctorId or matching integratedCares
      $match['$expr'] = {
        $or: [
          { $eq: ['$primaryDoctorId', doctorIdForIntegratedCares] },
          { $gt: [{ $size: '$matchingIntegratedCares' }, 0] },
        ],
      };
    }

    aggregation.push(
      { $match },
      { $setWindowFields: { output: { totalCount: { $count: {} } } } },
      {
        $addFields: {
          normalizedCreatedAt: {
            $cond: {
              if: { $eq: [{ $type: '$createdAt' }, 'string'] },
              then: { $dateFromString: { dateString: '$createdAt', onError: '$createdAt' } },
              else: '$createdAt',
            },
          },
        },
      },
      {
        $sort:
          sort.column === 'createdAt'
            ? { normalizedCreatedAt: sort.direction === SortDirection.Ascending ? 1 : -1 }
            : { [sort.column]: sort.direction === SortDirection.Ascending ? 1 : -1 },
      },
      { $skip: skip },
      { $limit: this.PAGINATION_DEFAULT }
    );
    return { sqlQuery, aggregation };
  }

  private createMatchQuery(field: string, values: string[], $match: any) {
    if (values.length > 1) {
      if (field === 'deactivationReason') {
        $match[field] = {
          $in: values.map(value => parseInt(value.toString().replace(`${field}:`, ''), 10)),
        };
      } else {
        $match[field] = {
          $in: values.map(value => value.toString().replace(`${field}:`, '')),
        };
      }
    } else {
      if (field === 'deactivationReason') {
        $match[field] = parseInt(values.toString().replace(`${field}:`, ''), 10);
      } else {
        $match[field] = values.toString().replace(`${field}:`, '');
      }
    }
    return $match;
  }

  private matchFieldIdQuery(query: string, field: string): { query: string; fieldValue: string } {
    const matchId = query.match(new RegExp(`${field}:([^\\s]+)`));
    const fieldValue = matchId ? matchId[1] : null;
    query = query.replace(new RegExp(`\\s*${field}:[^\\s]+`), '');
    return { query, fieldValue };
  }

  private isSearchTermUUID(searchTerm: string): boolean {
    const value = searchTerm.replace('*', '');
    return (
      !searchTerm.includes(':') &&
      !searchTerm.includes(' ') &&
      (validate(value) || new RegExp('^[0-9a-fA-F]{24}$').test(value))
    );
  }
}
